import { useState, useEffect, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import multiplayerService from '../../services/multiplayerService';
import { joinRoom, leaveRoom } from '../../services/websocketService';
import Button from '../ui/Button';
import Card from '../ui/Card';
import { avatarSrc } from '../avatar/avatarList';
import './WaitingRoom.css';

const WaitingRoom = ({ room, onGameStart, onLeave }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [participants, setParticipants] = useState(room.participants || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const isHost = room.host?.id === user?.id;
  const joinUrl = `${window.location.origin}/multiplayer/${room.room_code}`;

  // Handle WebSocket events
  const handlePlayerJoined = useCallback((data) => {
    setParticipants(prev => {
      const exists = prev.some(p => p.id === data.user.id);
      if (exists) return prev;
      return [...prev, {
        id: data.user.id,
        name: data.user.name,
        avatar: data.user.avatar,
        total_score: 0,
        is_host: false,
      }];
    });
  }, []);

  const handlePlayerLeft = useCallback((data) => {
    setParticipants(prev => prev.filter(p => p.id !== data.user.id));
    if (data.room_abandoned) {
      onLeave();
    }
  }, [onLeave]);

  const handlePlayerKicked = useCallback((data) => {
    setParticipants(prev => prev.filter(p => p.id !== data.user.id));
    if (data.user.id === user?.id) {
      navigate('/multiplayer');
    }
  }, [user?.id, navigate]);

  const handleGameStarted = useCallback((data) => {
    onGameStart(data);
  }, [onGameStart]);

  useEffect(() => {
    // Join WebSocket channel
    const channel = joinRoom(room.room_code, {
      onHere: (users) => {
        // Presence channel - users already in room
        console.log('Users in room:', users);
      },
      onJoining: (user) => {
        console.log('User joining:', user);
      },
      onLeaving: (user) => {
        console.log('User leaving:', user);
      },
      onPlayerJoined: handlePlayerJoined,
      onPlayerLeft: handlePlayerLeft,
      onPlayerKicked: handlePlayerKicked,
      onGameStarted: handleGameStarted,
    });

    return () => {
      leaveRoom(room.room_code);
    };
  }, [room.room_code, handlePlayerJoined, handlePlayerLeft, handlePlayerKicked, handleGameStarted]);

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(room.room_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleStartGame = async () => {
    if (participants.length < 2) {
      setError('Need at least 2 players to start');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await multiplayerService.startGame(room.room_code);
      if (response.success) {
        onGameStart(response.data);
      } else {
        setError(response.message || 'Failed to start game');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to start game');
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveRoom = async () => {
    setLoading(true);
    try {
      await multiplayerService.leaveRoom(room.room_code);
      onLeave();
    } catch (err) {
      console.error('Failed to leave:', err);
      onLeave(); // Leave anyway
    }
  };

  const handleKickPlayer = async (participantId) => {
    try {
      await multiplayerService.removeParticipant(room.room_code, participantId);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove player');
    }
  };

  return (
    <div className="waiting-room">
      <Card className="room-info-card">
        <h2>Room Code</h2>
        <div className="room-code-display" onClick={handleCopyCode}>
          <span className="room-code">{room.room_code}</span>
          <span className="copy-hint">{copied ? 'Copied!' : 'Click to copy'}</span>
        </div>
        <p className="share-hint">Share this code with your friends to join!</p>

        <div className="room-qr">
          <div className="room-qr-frame">
            <QRCodeSVG
              value={joinUrl}
              size={160}
              level="M"
              marginSize={2}
              bgColor="#ffffff"
              fgColor="#0f172a"
            />
          </div>
          <span className="room-qr-caption">Scan to join</span>
        </div>
      </Card>

      <Card className="settings-info">
        <h3>Game Settings</h3>
        <div className="settings-grid">
          <div className="setting-item">
            <span className="setting-label">Rounds</span>
            <span className="setting-value">{room.total_rounds}</span>
          </div>
          <div className="setting-item">
            <span className="setting-label">Time/Round</span>
            <span className="setting-value">{room.round_time_limit}s</span>
          </div>
          <div className="setting-item">
            <span className="setting-label">Max Players</span>
            <span className="setting-value">{room.max_players}</span>
          </div>
        </div>
      </Card>

      <Card className="participants-card">
        <h3>Players ({participants.length}/{room.max_players})</h3>
        <ul className="participants-list">
          {participants.map((participant) => (
            <li key={participant.id} className="participant-item">
              <span className="participant-avatar">
                {participant.avatar
                  ? <img src={avatarSrc(participant.avatar)} alt="" />
                  : participant.name.charAt(0).toUpperCase()}
              </span>
              <span className="participant-name">{participant.name}</span>
              {participant.is_host && (
                <span className="host-badge">Host</span>
              )}
              {participant.id === user?.id && (
                <span className="you-badge">You</span>
              )}
              {isHost && !participant.is_host && (
                <button
                  className="kick-btn"
                  onClick={() => handleKickPlayer(participant.id)}
                  title={`Remove ${participant.name}`}
                >
                  ✕
                </button>
              )}
            </li>
          ))}
        </ul>
        
        <div className="waiting-message">
          {isHost ? (
            <p>Waiting for players to join...</p>
          ) : (
            <p>Waiting for host to start the game...</p>
          )}
        </div>
      </Card>

      {error && <div className="error-message">{error}</div>}

      <div className="waiting-actions">
        {isHost ? (
          <Button
            variant="primary"
            size="lg"
            onClick={handleStartGame}
            disabled={loading || participants.length < 2}
          >
            {loading ? 'Starting...' : 'Start Game'}
          </Button>
        ) : (
          <p className="waiting-text">Waiting for host to start...</p>
        )}
        
        <Button
          variant="secondary"
          onClick={handleLeaveRoom}
          disabled={loading}
        >
          Leave Room
        </Button>
      </div>
    </div>
  );
};

export default WaitingRoom;
